import { z } from "zod";
import { isoDateString, isoDateTimeString, uuidSchema } from "./common";

// Inlined (not imported from identity.ts/orgs.ts) to avoid a schema import cycle
// — kyc is imported by orgs. Values must match those enums.
const changeRejectCodeSchema = z.enum([
  "DOC_UNREADABLE",
  "DOC_EXPIRED",
  "NAME_MISMATCH",
  "TAXCODE_MISMATCH",
  "WRONG_DOC_TYPE",
  "REP_ID_MISMATCH",
  "REP_NOT_VERIFIED",
  "DUPLICATE_TAXCODE",
  "OTHER",
]);
const orgLegalTypeSchema = z.enum(["INDIVIDUAL", "HOUSEHOLD", "COMPANY"]);

export const kycStatusSchema = z.enum([
  "NONE",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "EXPIRED",
]);

// ============================================================================
// Worker KYC: CCCD front/back + selfie
// ============================================================================
export const workerKycSubmissionSelfSchema = z.object({
  id: uuidSchema,
  status: kycStatusSchema,
  fullName: z.string(),
  cccdNumberMasked: z.string(),
  dob: isoDateString,
  rejectReason: z.string().nullable().optional(),
  submittedAt: isoDateTimeString,
  reviewedAt: isoDateTimeString.nullable().optional(),
  expiresAt: isoDateTimeString.nullable().optional(),
  // Signed read URLs so the worker can review the photos they submitted
  // (helps avoid the same mistake on resubmit). Nullable in case storage is
  // not configured or signing fails.
  cccdFrontSignedUrl: z.string().url().nullable().optional(),
  cccdBackSignedUrl: z.string().url().nullable().optional(),
  selfieSignedUrl: z.string().url().nullable().optional(),
  signedUrlExpiresAt: isoDateTimeString.nullable().optional(),
});

export const workerKycSubmissionAdminSchema =
  workerKycSubmissionSelfSchema.extend({
    userId: uuidSchema,
    userPhone: z.string().nullable().optional(),
    userEmail: z.string().nullable().optional(),
    cccdNumber: z.string(),
    cccdFrontSignedUrl: z.string().url(),
    cccdBackSignedUrl: z.string().url(),
    selfieSignedUrl: z.string().url(),
    signedUrlExpiresAt: isoDateTimeString,
  });

export const workerKycPresignRequestSchema = z.object({
  files: z
    .object({
      cccdFront: z.object({
        contentType: z.enum(["image/jpeg", "image/png"]),
        sizeBytes: z
          .number()
          .int()
          .positive()
          .max(10 * 1024 * 1024),
      }),
      cccdBack: z.object({
        contentType: z.enum(["image/jpeg", "image/png"]),
        sizeBytes: z
          .number()
          .int()
          .positive()
          .max(10 * 1024 * 1024),
      }),
      selfie: z.object({
        contentType: z.enum(["image/jpeg", "image/png"]),
        sizeBytes: z
          .number()
          .int()
          .positive()
          .max(10 * 1024 * 1024),
      }),
    })
    .strict(),
});

export const workerKycPresignResponseSchema = z.object({
  cccdFront: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  cccdBack: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  selfie: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  expiresAt: isoDateTimeString,
});

export const workerKycSubmitRequestSchema = z.object({
  cccdNumber: z.string().regex(/^[0-9]{9,12}$/, "CCCD 9-12 digits"),
  fullName: z.string().min(1).max(120),
  dob: isoDateString,
  cccdFrontKey: z.string().min(1),
  cccdBackKey: z.string().min(1),
  selfieKey: z.string().min(1),
  consentAccepted: z.literal(true),
});

export const workerKycSubmitResponseSchema = z.object({
  submission: workerKycSubmissionSelfSchema,
});

// ============================================================================
// Org KYC: tax code + business registration doc
// ============================================================================
export const orgKycSubmissionSelfSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  status: kycStatusSchema,
  legalName: z.string(),
  taxCode: z.string(),
  representativeName: z.string(),
  businessType: z.string(),
  legalType: orgLegalTypeSchema.nullable().optional(),
  registrationDocPublicUrl: z.string().nullable().optional(),
  taxDocPublicUrl: z.string().nullable().optional(),
  rejectReason: z.string().nullable().optional(),
  rejectCode: changeRejectCodeSchema.nullable().optional(),
  submittedAt: isoDateTimeString,
  reviewedAt: isoDateTimeString.nullable().optional(),
  expiresAt: isoDateTimeString.nullable().optional(),
});

export const orgKycSubmissionAdminSchema = orgKycSubmissionSelfSchema.extend({
  submittedByUserId: uuidSchema,
  registrationDocSignedUrl: z.string().url(),
  taxDocSignedUrl: z.string().url().nullable().optional(),
  signedUrlExpiresAt: isoDateTimeString,
});

export const orgKycPresignRequestSchema = z.object({
  registrationDoc: z.object({
    contentType: z.enum(["image/jpeg", "image/png", "application/pdf"]),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(20 * 1024 * 1024),
  }),
  taxDoc: z
    .object({
      contentType: z.enum(["image/jpeg", "image/png", "application/pdf"]),
      sizeBytes: z
        .number()
        .int()
        .positive()
        .max(20 * 1024 * 1024),
    })
    .optional(),
});

export const orgKycPresignResponseSchema = z.object({
  registrationDoc: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  taxDoc: z
    .object({ uploadUrl: z.string().url(), key: z.string() })
    .nullable()
    .optional(),
  expiresAt: isoDateTimeString,
});

export const orgKycSubmitRequestSchema = z.object({
  legalName: z.string().min(1).max(200),
  taxCode: z.string().min(8).max(20),
  representativeName: z.string().min(1).max(120),
  businessType: z.string().min(1).max(60),
  registrationDocKey: z.string().min(1),
  taxDocKey: z.string().min(1).optional(),
  consentAccepted: z.literal(true),
});

export const orgKycSubmitResponseSchema = z.object({
  submission: orgKycSubmissionSelfSchema,
});

export const kycRejectRequestSchema = z.object({
  reason: z.string().min(1).max(500),
  // Standardized reason code (SCRUM-74). Optional for backward compat; the org
  // KYC reject UI sends it, worker KYC (free-text) may omit.
  rejectCode: changeRejectCodeSchema.optional(),
});

// Org KYC list filter (SCRUM-74) — status defaults to PENDING server-side.
export const adminOrgKycListQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: kycStatusSchema.optional(),
  legalType: orgLegalTypeSchema.optional(),
});

export type KycStatus = z.infer<typeof kycStatusSchema>;
export type WorkerKycSubmissionSelf = z.infer<
  typeof workerKycSubmissionSelfSchema
>;
export type WorkerKycSubmissionAdmin = z.infer<
  typeof workerKycSubmissionAdminSchema
>;
export type WorkerKycPresignRequest = z.infer<
  typeof workerKycPresignRequestSchema
>;
export type WorkerKycPresignResponse = z.infer<
  typeof workerKycPresignResponseSchema
>;
export type WorkerKycSubmitRequest = z.infer<
  typeof workerKycSubmitRequestSchema
>;
export type WorkerKycSubmitResponse = z.infer<
  typeof workerKycSubmitResponseSchema
>;
export type OrgKycSubmissionSelf = z.infer<typeof orgKycSubmissionSelfSchema>;
export type OrgKycSubmissionAdmin = z.infer<typeof orgKycSubmissionAdminSchema>;
export type OrgKycPresignRequest = z.infer<typeof orgKycPresignRequestSchema>;
export type OrgKycPresignResponse = z.infer<typeof orgKycPresignResponseSchema>;
export type OrgKycSubmitRequest = z.infer<typeof orgKycSubmitRequestSchema>;
export type OrgKycSubmitResponse = z.infer<typeof orgKycSubmitResponseSchema>;
export type KycRejectRequest = z.infer<typeof kycRejectRequestSchema>;
export type AdminOrgKycListQuery = z.infer<typeof adminOrgKycListQuerySchema>;
